import { Component, type ReactNode } from 'react'

import { useCrash } from '@orus.eu/pharaoh'

import { LocalLoadingState } from '../molecules/local-loading-state.tsx'

type SafeErrorBoundaryProps = {
  children: ReactNode
}

type SafeErrorBoundaryState = {
  hasError: boolean
}

/**
 * Error boundary that catches ALL errors thrown during render and handles them properly by:
 * 1. Showing a temporary loading state
 * 2. Calling crash() to schedule proper error handling
 *
 * This ensures users see the proper CrashPage (with translations, chat button, etc.)
 * instead of a blank page or the hardcoded fallback.
 *
 * This boundary should be placed near the root of the app to catch any render errors.
 */
class SafeErrorBoundaryInner extends Component<
  SafeErrorBoundaryProps & { crash: (error: Error) => void },
  SafeErrorBoundaryState
> {
  override state: SafeErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): SafeErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: unknown): void {
    // Schedule proper error handling via crash()
    // This will trigger the root error boundary on the next render cycle,
    // showing the proper CrashPage with translations and chat support
    if (error instanceof Error) {
      this.props.crash(error)
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // Show temporary loading state while crash() is being processed
      return <LocalLoadingState />
    }
    return this.props.children
  }
}

/**
 * Wrapper that provides the crash function from useCrash hook to the class component.
 */
export function SafeErrorBoundary({ children }: SafeErrorBoundaryProps): ReactNode {
  const crash = useCrash()
  return <SafeErrorBoundaryInner crash={crash}>{children}</SafeErrorBoundaryInner>
}
